import Duck from 'extensible-duck'

const CartItemDuck = new Duck({
  namespace: 'content',
  store: 'cartData',
  types: [
    'UPDATE',
    'SELECT_SIZE',
    'IS_SOLD_OUT',
    'UPDATE_SNACKBAR',
    'UPDATE_VOUCHER_ERROR',
    'UPDATE_FREE_GIFT',
    'REMOVING_ITEM',
    'UPDATING_VOUCHER',
    'TOGGLE_ITEM_SELECTED',
    'SET_ITEMS_SELECTED',
    'RESTORE_ITEMS_SELECTED',
  ],
  initialState: {
    cartOpen: false,
    couponUsed: null,
    freeGiftOpen: false,
    sizeSelected: false,
    isStoreCreditUsed: false,
    hasSoldOutItem: false,
    snackbarMessage: {
      text: '',
      time: 0,
      type: null,
    },
    voucherErrorMessage: '',
    isFreeGiftAdded: false,
    removingItem: null,
    updatingVoucher: false,
    // UI-only for now: which selectable line items ("<id_product>-<id_
    // product_attribute>" keys) are checked. Drives "Select all (n/m)" and
    // the per-item checkboxes together. Does not yet change the charged
    // total or what checkout submits -- that needs the cart/order APIs to
    // support a subset of items, which they don't today. Undefined means
    // "everything selected" (the default), so a freshly loaded cart with
    // items this state has never seen still shows fully checked.
    selectedItemKeys: undefined,
  },
  reducer: (state, action, duck) => {
    switch (action.type) {
      case duck.types.UPDATING_VOUCHER:
        return { ...state, updatingVoucher: action.busy }
      case duck.types.REMOVING_ITEM:
        return { ...state, removingItem: action.item }
      case duck.types.SELECT_SIZE:
        return { ...state, sizeSelected: !state.sizeSelected }
      case duck.types.UPDATE:
        return {
          ...state,
          cartOpen: action.cartOpen,
          couponUsed: action.couponUsed,
          freeGiftOpen: action.freeGiftOpen,
          isStoreCreditUsed: action.isStoreCreditUsed,
        }
      case duck.types.IS_SOLD_OUT:
        return { ...state, hasSoldOutItem: action.hasSoldOutItem }
      case duck.types.UPDATE_SNACKBAR:
        return { ...state, snackbarMessage: action.snackbarMessage }
      case duck.types.UPDATE_VOUCHER_ERROR:
        return { ...state, voucherErrorMessage: action.voucherErrorMessage }
      case duck.types.UPDATE_FREE_GIFT:
        return { ...state, isFreeGiftAdded: action.isFreeGiftAdded }
      case duck.types.TOGGLE_ITEM_SELECTED: {
        const current = state.selectedItemKeys ?? action.allKeys
        const isSelected = current.includes(action.key)
        return {
          ...state,
          selectedItemKeys: isSelected
            ? current.filter((key) => key !== action.key)
            : [...current, action.key],
        }
      }
      case duck.types.SET_ITEMS_SELECTED:
        return {
          ...state,
          selectedItemKeys: action.selected ? undefined : [],
        }
      // Puts selectedItemKeys back exactly as it was, including the
      // `undefined` ("everything selected") default -- used to undo the
      // optimistic select-all/deselect-all flip when the batched
      // selectable-cart call fails (cartDuck.creators.
      // setMultipleItemsSelected). SET_ITEMS_SELECTED can't do this: it
      // only expresses all-or-nothing, so it can't restore a partial
      // selection the shopper had built up before toggling.
      case duck.types.RESTORE_ITEMS_SELECTED:
        return { ...state, selectedItemKeys: action.selectedItemKeys }
      default:
        return state
    }
  },
  creators: (duck) => ({
    update: (options) => ({
      type: duck.types.UPDATE,
      cartOpen: options?.cartOpen,
      couponUsed: options?.couponUsed,
      freeGiftOpen: options?.freeGiftOpen,
      isStoreCreditUsed: options?.isStoreCreditUsed,
    }),
    toggleSelectSize: () => ({ type: duck.types.SELECT_SIZE }),
    checkSoldOutItem: (options) => ({
      type: duck.types.IS_SOLD_OUT,
      hasSoldOutItem: options?.hasSoldOutItem,
    }),
    updateSnackbar: (text, type) => ({
      type: duck.types.UPDATE_SNACKBAR,
      snackbarMessage: {
        text,
        time: Date.now(),
        type,
      },
    }),
    updateVoucherErrorMessage: (text) => ({
      type: duck.types.UPDATE_VOUCHER_ERROR,
      voucherErrorMessage: text,
    }),
    updateIsFreeGiftAdded: (isFreeGiftAdded) => ({
      type: duck.types.UPDATE_FREE_GIFT,
      isFreeGiftAdded,
    }),
    setRemovingItem: (item) => ({ type: duck.types.REMOVING_ITEM, item }),
    setUpdatingVoucher: (busy) => ({ type: duck.types.UPDATING_VOUCHER, busy }),
    toggleItemSelected: (key, allKeys) => ({
      type: duck.types.TOGGLE_ITEM_SELECTED,
      key,
      allKeys,
    }),
    setItemsSelected: (selected) => ({
      type: duck.types.SET_ITEMS_SELECTED,
      selected,
    }),
    restoreItemsSelected: (selectedItemKeys) => ({
      type: duck.types.RESTORE_ITEMS_SELECTED,
      selectedItemKeys,
    }),
  }),
})

export default CartItemDuck
